import { layerLabel } from '@/utils/layerLabel';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Eye, EyeOff, Palette, Star, Tag } from 'lucide-react';
import LayerStyleEditor from './LayerStyleEditor';
import { Layer } from '../../types/api';
import { cn } from '../../utils/cn';
import { AddToGroupMenu } from './groups/AddToGroupMenu';
import { platformRef } from './groups/useLayerGroups';

interface LayerItemProps {
  layer: Layer;
  visible: boolean;
  isFavorite: boolean;
  isRadio?: boolean;
  hasLabel?: boolean;
  labelVisible?: boolean;
  onVisibilityToggle: (layerId: number) => void;
  onFavoriteToggle: (layerId: number) => void;
  onLabelToggle?: (layerId: number) => void;
}

export const LayerItem: React.FC<LayerItemProps> = ({
  layer,
  visible,
  isFavorite,
  isRadio: _isRadio = false,
  hasLabel = false,
  labelVisible = false,
  onVisibilityToggle,
  onFavoriteToggle,
  onLabelToggle,
}) => {
  const { t, i18n } = useTranslation();
  const displayName = layerLabel(layer) || t('layers.unnamed');
  const [styling, setStyling] = React.useState(false);

  return (
    <div
      className={cn(
        'flex items-center justify-between px-2 py-1.5 rounded text-xs',
        'transition-all duration-150',
        'group cursor-pointer',
        visible
          ? 'bg-gn-tertiary border border-gn-light'
          : 'bg-transparent border border-transparent hover:bg-gn-tertiary hover:border-gn-light',
      )}
      role="treeitem"
      onClick={() => onVisibilityToggle(layer.id)}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Visibility indicator */}
        <span
          className={cn(
            'flex-shrink-0 transition-colors',
            visible
              ? 'text-emerald-400'
              : 'text-gn-muted group-hover:text-gn-secondary',
          )}
        >
          {visible ? <Eye size={13} /> : <EyeOff size={13} />}
        </span>

        {/* Layer Name */}
        <span
          className={cn(
            'flex-1 truncate transition-colors text-[11px]',
            visible ? 'text-gn-primary font-medium' : 'text-gn-secondary group-hover:text-gn-primary',
          )}
          title={displayName}
        >
          {displayName}
        </span>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {/* Label Toggle — visible when layer has labels */}
        {hasLabel && onLabelToggle && (
          <button
            onClick={() => onLabelToggle(layer.id)}
            className={cn(
              'p-0.5 rounded transition-colors',
              labelVisible
                ? 'text-gn-warning hover:text-gn-warning'
                : 'text-gn-muted hover:text-gn-warning opacity-0 group-hover:opacity-100',
            )}
            title={labelVisible ? t('layers.hide_labels') : t('layers.show_labels')}
          >
            <Tag size={11} />
          </button>
        )}

        <AddToGroupMenu item={{ kind: 'platform', ref: platformRef(layer) }} />
        {/* Style: the person's own view of the layer (and, for the platform's staff, its main style) */}
        <button
          onClick={() => setStyling(true)}
          className="p-0.5 rounded transition-colors text-gn-muted hover:text-gn-primary opacity-0 group-hover:opacity-100 focus:opacity-100"
          title={t('styles.open')}
          aria-label={t('styles.open')}
        >
          <Palette size={11} />
        </button>

        {/* Favorite Toggle */}
        <button
          onClick={() => onFavoriteToggle(layer.id)}
          className={cn(
            'p-0.5 rounded transition-colors',
            isFavorite
              ? 'text-gn-warning hover:text-gn-warning'
              : 'text-gn-muted hover:text-gn-warning opacity-0 group-hover:opacity-100',
          )}
          title={isFavorite ? t('layers.remove_from_favorites') : t('layers.add_to_favorites')}
        >
          <Star size={11} fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
        {/* inside the actions, whose clicks do not reach the row: the drawer is a portal, but React events still bubble */}
        {styling && (
          <LayerStyleEditor platformLayer={layer} lang={(i18n.language || 'en').substring(0, 2)} onClose={() => setStyling(false)} />
        )}
      </div>
    </div>
  );
};
